<script setup lang="ts">
import { ref, computed, watch } from "vue";
import "swiper/css";
import "swiper/css/pagination";
import { Swiper, SwiperSlide } from "swiper/vue";
import { Thumbs } from "swiper/modules";
import type { Swiper as ISwiper } from "swiper/types";
import VHidden from "@magnit/core/src/components/VHidden/VHidden.vue";
import VSliderDots from "@magnit/core/src/components/VSliderDots/VSliderDots.vue";
import VProductAdultImage from "@magnit/unit-catalog/src/components/VProductAdultImage.vue";
import type { IProductDetailsGalleryProps } from "../types/product-details";
import { collectCardDetailImagesPayload } from "../utils/analytics";
import type { AnalyticsEventArguments } from "../types/analytics";
import useProduct from "../composables/useProduct";

const props = withDefaults(defineProps<IProductDetailsGalleryProps>(), {
  slideIndex: 0,
});
const emit = defineEmits<{
  "update:slideIndex": [value: number];
  analytics: AnalyticsEventArguments;
}>();

const mainSwiper = ref<ISwiper | null>(null);
const thumbsSwiper = ref<ISwiper | null>(null);
const slideIndexRef = ref(props.slideIndex);
const currentSlideIndex = computed({
  get: () => slideIndexRef.value,
  set: (v: number) => {
    slideIndexRef.value = v;
    emit("update:slideIndex", v);
  },
});

const { productName, productImageUrl, productGallery } = useProduct(
  props.product,
);

const galleryImages = computed(() => {
  if (productGallery.value.length) return productGallery.value;
  if (productImageUrl.value) return [productImageUrl.value];
  return [];
});
const imagesCount = computed(() => galleryImages.value.length || 0);

const setMainSwiper = (sw: ISwiper) => {
  mainSwiper.value = sw;
};
const setThumbsSwiper = (sw: ISwiper) => {
  thumbsSwiper.value = sw;
};
const handleSlideChange = (swiper: ISwiper) => {
  currentSlideIndex.value = swiper.realIndex;
};
const handleDotsClick = (idx: number) => {
  if (!mainSwiper.value) return;
  mainSwiper.value.slideTo(idx);
};

const handleThumbnailClick = (position: number) => {
  emit(
    "analytics",
    "CardDetail:ImageList:Click",
    collectCardDetailImagesPayload(position, props.product, props.categoryName),
  );
};

watch(
  () => props.slideIndex,
  (next) => {
    if (next === slideIndexRef.value) return;
    slideIndexRef.value = next;
  },
);
</script>

<template>
  <section v-if="imagesCount" class="product-details-gallery">
    <meta
      v-for="(image, index) in galleryImages"
      :key="index"
      itemprop="image"
      :content="image"
    />
    <div class="product-details-gallery__inner">
      <VHidden
        v-if="galleryImages?.length > 1"
        :hide="{ xs: true, l: false }"
        class="product-details-gallery__thumb-swiper__outer"
      >
        <Swiper
          class="product-details-gallery__thumb-swiper"
          :slides-per-view="7"
          :space-between="4"
          :modules="[Thumbs]"
          watch-slides-progress
          direction="vertical"
          @swiper="setThumbsSwiper"
        >
          <SwiperSlide
            v-for="(thumbnail, index) in galleryImages"
            :key="index"
            class="product-details-gallery__thumbnail"
            @click="handleThumbnailClick(index + 1)"
          >
            <VProductAdultImage :blurred="blurred" thumbnail hide-label>
              <img :alt="productName" :src="thumbnail" />
            </VProductAdultImage>
          </SwiperSlide>
        </Swiper>
      </VHidden>
      <div class="product-details-gallery__main-swiper__outer">
        <div class="product-details-gallery__main-swiper__inner">
          <Swiper
            class="product-details-gallery__main-swiper"
            :slides-per-view="1"
            :space-between="0"
            :modules="[Thumbs]"
            :thumbs="{ swiper: thumbsSwiper }"
            @slide-change="handleSlideChange"
            @swiper="setMainSwiper"
          >
            <SwiperSlide
              v-for="(image, index) in galleryImages"
              :key="index"
              class="product-details-gallery__slide"
            >
              <VProductAdultImage :blurred="blurred">
                <img
                  class="product-details-gallery__slide-image"
                  :alt="productName"
                  :src="image"
                />
              </VProductAdultImage>
            </SwiperSlide>
          </Swiper>
          <VHidden
            v-if="imagesCount > 1"
            :hide="{ l: false, xl: true }"
            class="product-details-gallery__slide-dots-container"
          >
            <VSliderDots
              v-model="currentSlideIndex"
              class="product-details-gallery__slide-dots"
              size="l"
              :count="imagesCount"
              :visible-count="imagesCount"
              @update:model-value="handleDotsClick"
            />
          </VHidden>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped lang="postcss">
.product-details-gallery {
  &__inner {
    display: flex;
    width: 100%;
    background-color: var(--pl-surface-muted-default);

    @media (--pl-viewport-from-m) {
      background-color: initial;
    }

    @media (--pl-viewport-from-l) {
      display: flex;
      gap: var(--pl-unit-x1);
    }
  }

  &__main-swiper {
    width: 100%;
    height: 320px;

    @media (--pl-viewport-from-m) {
      height: 372px;
    }

    @media (--pl-viewport-from-l) {
      height: 388px;
    }

    &__outer {
      flex-grow: 1;
      display: flex;
      width: 100%;
      position: relative;

      @media (--pl-viewport-from-l) {
        width: calc(100% - 48px - var(--pl-unit-x1));
        height: 388px;
      }
    }

    &__inner {
      width: 100%;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      background-color: var(--pl-surface-muted-default);
      border-radius: var(--pl-unit-x6);
      overflow: hidden;
    }
  }

  &__thumb-swiper {
    height: 100%;

    &__outer {
      padding: var(--pl-unit-x1) 0;
      width: 48px;
      height: 372px;
    }
  }

  &__slide,
  &__thumbnail {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;
  }

  &__slide {
    width: 320px;

    @media (--pl-viewport-from-m) {
      height: 372px;
    }

    @media (--pl-viewport-from-l) {
      height: 388px;
    }

    &-image {
      width: 100%;
      height: 100%;
      object-fit: contain;
      filter: brightness(
        0.96
      ); /* маскирует жоский белый фон на картинке, лучше вписывается в новый дизайн */
    }

    &-dots-container {
      z-index: var(--pl-z-index-level1);
      margin-top: calc(-1 * var(--pl-unit-x6));
    }

    &-dots {
      margin: 0;

      :deep(.pl-slider-dots__inner) {
        width: auto !important;
      }
    }
  }

  &__thumbnail {
    width: 48px;
    height: 48px;
    border: 1px solid transparent;
    transition: border 0.2s;
    cursor: pointer;

    img {
      width: var(--pl-unit-x10);
      height: var(--pl-unit-x10);
      object-fit: contain;
    }

    &.swiper-slide-thumb-active {
      border: 1px solid #b8b8b8;
      border-radius: var(--pl-unit-x1);
    }
  }
}
</style>
